/**
 * ADRES LANDINGU MODUŁU OBSŁUGI — jedna reguła dla nagłówka aplikacji i kolejki zgłoszeń.
 *
 * [polecenie właściciela 2026-09-30, B1] „wywal i daj przycisk powrotu do sekcji /obsluga-XXXX z panelu
 * obsługi zgłoszeń”. Do 30.09 ten sam moduł dynamiczny miał DWA różne adresy, zależnie od tego, kto pytał:
 *   • przycisk modułu w nagłówku (Header.js) prowadził na `/obsluga-<kod>` (trasa ModuleSlugRoute w App.js),
 *   • przycisk powrotu w kolejce zgłoszeń prowadził na `/obsluga-it/<kod>` (trasa itManagementModule).
 * Obie trasy działają, ale człowiek wracał z kolejki na inny ekran niż ten, z którego do niej wszedł.
 * TERAZ obie strony pytają tę funkcję — moduł dynamiczny ma jeden adres: `/obsluga-<kod>`.
 *
 * Moduł `itsm` (i jego kod archiwalny `tickets`) to landing obsługi IT, a nie `/obsluga-itsm`.
 * Moduł ze stroną WBUDOWANĄ (np. HR → `hrManagement`) rozstrzyga wołający przez `localizedPath(viewModuleCode)`,
 * bo tylko on wie, czy dana osoba ma klucz tej strony (Header) albo czy kolejka jest kolejką tej strony.
 *
 * Plik jest czysty (bez Reacta) — zapadki: pages/__tests__/powrotWNaglowkuKolejki.0930.test.js,
 * components/__tests__/obslugaWNaglowkuNaKolejce.1001.test.js.
 */
import { localizedPath } from "../hooks/useLocalizedNavigate";
import { ROUTE_MAP } from "../constants/routes";
import { pathStartsWith } from "./routeHelpers";
import { czyRouterOdmawiaKafelka, getTileBySlug } from "../constants/adminTilesConfig";
import { sciezkaProcedury } from "../components/Polityki/strona/etapy";
import { rejestrStrony, stronaWPanelu, tytulStrony } from "../config/obszary";
import { bezpiecznieDekoduj } from "../components/common/Obszary/adresyObszarow";

/** Kody modułu, które oznaczają obsługę IT (alias archiwalny jak na serwerze: `tickets` = `itsm`). */
const KODY_IT = ["itsm", "tickets"];

/**
 * Adres landingu modułu obsługi BEZ strony wbudowanej.
 * @param {string|null|undefined} kod kod modułu zgłoszeń (TicketModule.Code)
 * @returns {string|null} `/obsluga-it` (lokalizowany) dla IT, `/obsluga-<kod>` dla pozostałych, `null` bez kodu
 */
export function adresModuluObslugi(kod) {
    const k = String(kod ?? "").trim();
    if (!k) return null;
    if (KODY_IT.includes(k.toLowerCase())) return localizedPath("itManagement");
    return `/obsluga-${k}`;
}

/**
 * [poprawka właściciela 2026-10-01] KTÓREJ STRONY WBUDOWANEJ KOLEJKĄ JEST TEN ADRES — czysta funkcja dla nagłówka aplikacji.
 *
 * „Przycisk «Obsługa IT» ma być w HEADER, a nie w tej belce”. Na stronie kolejki zgłoszeń nagłówek pokazuje wprost przycisk
 * modułu tej kolejki („Obsługa IT”, „Obsługa HR”) prowadzący na jego landing — także wtedy, gdy osoba ma kilka modułów
 * i poza kolejką widzi jeden przycisk „Obsługa N” z listą. Oba języki adresów (PL/EN) przez `pathStartsWith`.
 *   • kolejka IT (`tickets`, także karta zgłoszenia `/obsluga-it/zgloszenia/:id`) → `"itManagement"`,
 *   • kolejka HR (`hrTickets`)                                                    → `"hrManagement"`,
 *   • każdy inny adres                                                            → `null`.
 * Wynik porównuje się z `viewModuleCode` modułu (IT: także moduł `itsm`/`tickets` bez strony wbudowanej).
 * @param {string|null|undefined} pathname
 * @returns {"itManagement"|"hrManagement"|null}
 */
export function widokKolejkiZAdresu(pathname) {
    const p = String(pathname ?? "");
    if (!p) return null;
    if (pathStartsWith(p, "hrTickets")) return "hrManagement";
    if (pathStartsWith(p, "tickets")) return "itManagement";
    return null;
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════
 * [polecenie właściciela 2026-10-02, EB-1] PRZYCISK „POWRÓT” W NAGŁÓWKU APLIKACJI — CEL „POZIOM WYŻEJ” Z ADRESU.
 *
 * „w /obsluga-it/zgloszenia w header, tak samo jak w admin, ale też dla pozostałych modułów, dodaj przycisk Powrót
 *  z odpowiednią nawigacją powrotu do modułu”.
 *
 * BYŁO ŹLE: przycisk widział tylko ADMINISTRATOR (ranga 3) na /admin/*, /e-teczka, /itam/*, /protocols, a prowadził przez
 * `navigate(-1)` — historię przeglądarki. Po F5, po wklejeniu adresu albo po wejściu z linku w mailu historia jest pusta
 * lub prowadzi poza aplikację; po przełączeniu zakładki w panelu (replace) cofał o jedną zakładkę, nie do /admin.
 * Delegat z kluczem kafelka (bez rangi) nie miał przycisku wcale; na kolejce zgłoszeń, w Wykazie KSC, w Moim Dziale — nikt.
 *
 * TERAZ cel liczy się WYŁĄCZNIE z adresu (i z `location.state.from`, gdy landing obsługi sam go ustawił), więc jest ten sam
 * po odświeżeniu, po linku i po przełączeniu języka. Wszystkie porównania przez ROUTE_MAP (PL i EN jednocześnie), nigdy gołym
 * napisem; adresy celu przez `localizedPath` liczone W MOMENCIE wywołania (po zmianie języka Header liczy od nowa z nowego
 * pathname). Brak celu (`null`) = brak przycisku: pulpit, landingi /obsluga-it, /obsluga-hr, /obsluga-<kod>, aktualności…
 *
 * Zapadki: utils/__tests__/celPowrotuZAdresu.1003.test.js (tabela adres → cel),
 *          components/__tests__/powrotWNaglowkuWszystkichModulow.1003.test.js (montaż nagłówka, zero `navigate(-1)`).
 * ═══════════════════════════════════════════════════════════════════════════════════════════ */

/** Napisy celu na przycisku (po polsku także przy adresach EN — reszta nagłówka jest po polsku). Brak wpisu → „Powrót”. */
const NAZWY_CELOW = {
    itManagement: "Obsługa IT",
    hrManagement: "Obsługa HR",
    tickets: "Zgłoszenia",
    hrTickets: "Zgłoszenia HR",
    admin: "Panel administracyjny",
    // [domknięcie eb 2026-10-04] Ta sama nazwa co kafelek /admin i przycisk na stronie definicji czasu pracy.
    adminEmployees: "Kartoteka pracowników",
    itamOverview: "Przegląd ITAM",
    wykazKsc: "Wykaz KSC",
    myDept: "Mój Dział",
    polityki: "Procedury",
    dashboard: "Pulpit",
};

const ETYKIETA_DOMYSLNA = "Powrót";

/** Oba adresy (PL/EN) wpisu ROUTE_MAP — bez duplikatu, gdy są równe (np. /admin, /e-teczka). */
const adresyTrasy = (routeKey) => {
    const r = ROUTE_MAP[routeKey];
    if (!r) return [];
    return r.pl === r.en ? [r.pl] : [r.pl, r.en];
};

/** Adres DOKŁADNIE równy statycznemu wpisowi (PL lub EN). */
const dokladnieTrasa = (pathname, routeKey) => adresyTrasy(routeKey).some((a) => !a.includes(":") && a === pathname);

/**
 * Adres leży POD statycznym wpisem (granica segmentu: „/obsluga-hr” łapie „/obsluga-hr/wnioski”, ale nie „/obsluga-hr2”).
 * Celowo ostrzejsze niż `pathStartsWith` (goły prefiks) — tu rozstrzyga się, do którego drzewa adres należy.
 */
const podTrasa = (pathname, routeKey) =>
    adresyTrasy(routeKey).some((a) => !a.includes(":") && pathname.startsWith(a + "/"));

/** Dopasowanie do wpisu PARAMETRYCZNEGO (`:id`, `:moduleCode`…) — zwraca parametry albo null. Wzorzec jak w translateCurrentPath. */
const parametryTrasy = (pathname, routeKey) => {
    for (const wzor of adresyTrasy(routeKey)) {
        if (!wzor.includes(":")) continue;
        const regex = new RegExp("^" + wzor.replace(/:[^/]+/g, "([^/]+)") + "$");
        const m = pathname.match(regex);
        if (!m) continue;
        const nazwy = [...wzor.matchAll(/:([^/]+)/g)].map((x) => x[1]);
        // [poprawka po przeglądzie 2026-10-06] Dekodowanie BEZ wyjątku: adres wpisany z ręki ze zniekształconym kodowaniem
        // procentowym (`/admin/100%/x`, `…/%zz`) nie może położyć nagłówka aplikacji, który liczy z niego „Powrót”.
        return Object.fromEntries(nazwy.map((n, i) => [n, bezpiecznieDekoduj(m[i + 1])]));
    }
    return null;
};

/**
 * Statyczne segmenty bezpośrednio pod landingiem obsługi IT — PL i EN, ZBUDOWANE z ROUTE_MAP, nie spisane ręcznie
 * (zgloszenia/tickets, sejf/vault, procedury/procedures…). Segment spoza tego zbioru pod /obsluga-it/ to KOD modułu dynamicznego
 * (trasa itManagementModule); bez tego wykluczenia /obsluga-it/procedury dostałoby cel /obsluga-procedury, który nie istnieje.
 */
const statyczneSegmentyObslugiIt = () => {
    const zbior = new Set();
    for (const r of Object.values(ROUTE_MAP)) {
        for (const jezyk of ["pl", "en"]) {
            const baza = ROUTE_MAP.itManagement[jezyk] + "/";
            const adres = r[jezyk];
            if (!adres || !adres.startsWith(baza)) continue;
            const segment = adres.slice(baza.length).split("/")[0];
            if (segment && !segment.startsWith(":")) zbior.add(segment);
        }
    }
    return zbior;
};

const celTrasy = (routeKey, etykieta) => ({ cel: localizedPath(routeKey), etykieta: etykieta ?? NAZWY_CELOW[routeKey] ?? ETYKIETA_DOMYSLNA });

/** Cel dla modułu obsługi po kodzie: IT (itsm/tickets) → landing /obsluga-it z jego nazwą, inny → /obsluga-<kod>. */
const celModulu = (kod) => {
    const adres = adresModuluObslugi(kod);
    if (!adres) return null;
    if (adres === localizedPath("itManagement")) return celTrasy("itManagement");
    return { cel: adres, etykieta: ETYKIETA_DOMYSLNA };
};

const bezOstatniegoSegmentu = (adres) => adres.replace(/\/[^/]+$/, "");

/**
 * [ui1006 2026-10-06, polecenie właściciela 5.10: cztery strony panelu od zera, każde miejsce pod własnym adresem]
 * STRONA WIELOOBSZAROWA PANELU, we wnętrzu której leży adres: `/admin/<slug>/<miejsce>` strony z rejestru obszarów
 * (config/obszary) — Procedury, Integracje, Office 365. Sam `/admin/<slug>` (start strony) NIE jest wnętrzem.
 * Wykaz KSC ma własne drzewo `/wykaz-ksc/…` (reguła wyżej), więc tu go nie ma.
 * @returns {object|null} rejestr strony albo null
 */
const stronaObszarowZAdresu = (pathname) => {
    for (const panel of adresyTrasy("admin")) {
        if (!pathname.startsWith(panel + "/")) continue;
        const [slug, ...reszta] = pathname.slice(panel.length + 1).split("/");
        if (!slug || reszta.length === 0) return null;
        const rejestr = rejestrStrony(bezpiecznieDekoduj(slug));
        return rejestr && stronaWPanelu(rejestr) ? rejestr : null;
    }
    return null;
};

/** Adres hubu „Mój Dział” (bez zakładki): wpis myDeptOverview bez ostatniego segmentu — /moj-dzial | /my-dept (brak klucza w ROUTE_MAP). */
const adresHubuMojegoDzialu = () => bezOstatniegoSegmentu(localizedPath("myDeptOverview"));

/**
 * CEL PRZYCISKU „POWRÓT” dla adresu — czysta funkcja (bez Reacta).
 *
 * OSŁONA KLUCZA (przegląd EB-1, 2026-10-03): przycisk nie może kłamać — nie prowadzi na ekran „Brak dostępu”.
 * Landingi drzewa IT (/obsluga-it, /obsluga-it/:kod, /obsluga-<kod>, ModuleSlugRoute) wymagają klucza TicketView,
 * landing /obsluga-hr wymaga HrAccess (App.js). Bez klucza do landingu celem jest PULPIT. Dotyczy to też stron, na które
 * sam klucz landingu nie jest potrzebny: księgowa z samym HrAccounting na /obsluga-hr/rozliczenia, compliance na
 * /obsluga-hr/sygnalista (SygnalistaManage, HrAccess celowo nie wystarcza), agent z samym TicketView na kolejce HR,
 * członek modułu bez TicketView na /obsluga-it/procedury — każdy dostaje „Pulpit”, nie wygaszony landing.
 *
 * [domknięcie eb 2026-10-04] OSŁONY DOMKNIĘTE (krytyk wdrożenia eb):
 *   • landing /obsluga-it to TicketView + WIDOCZNOŚĆ modułu itManagement (App.js) — jak HR; bez tego agent z ukrytym modułem IT
 *     dostawał „← Obsługa IT” prowadzące na „Moduł ukryty”. Landingi modułów dynamicznych (/obsluga-<kod>) stoją za samym TicketView;
 *   • strony ITAM wracają na „Przegląd ITAM” tylko z kluczem przeglądu (ItamDeviceView LUB ItamInvoiceView) — osoba z samym
 *     ItamLicenseView / ItamTeamViewerView / AdminAddressBook (karty SIM) idzie poziom wyżej: landing IT albo pulpit;
 *   • `stateFrom: "admin"` — wejście z KAFELKA panelu /admin na osobną stronę (E-teczka, ITAM, kartoteka) wraca do panelu,
 *     „tak samo jak w admin”; panel jest otwarty dla każdego zalogowanego (App.js), więc ten cel nie kończy się odmową;
 *   • ekrany, które mają WŁASNY przycisk powrotu, dostają w nagłówku TEN SAM cel: karta decyzji otwarta z okna procedury (?doc),
 *     rejestracja decyzji z papieru, szczegóły analityki czasu pracy, procedury modułu (strona woła tę samą funkcję).
 *
 * @param {string|null|undefined} pathname bieżący `location.pathname`
 * @param {object} [opcje] — najprościej `{ stateFrom, search, ...oslonyPowrotu(auth) }`
 *   stateFrom — `location.state.from`: landing obsługi (ItManagementPage / HrManagementPage) albo panel /admin (AdminPanelPage)
 *   ustawia go przy kafelku prowadzącym na osobną stronę — wygrywa, bo człowiek ma wrócić tam, skąd przyszedł;
 *   search — `location.search` (karta decyzji i rejestracja decyzji czytają `?doc=ID` tak samo jak ich strony);
 *   canAccessTickets — klucz TicketView (bramka landingów modułów dynamicznych);
 *   canAccessItManagement — TicketView + widoczność modułu itManagement (bramka /obsluga-it; domyślnie = canAccessTickets);
 *   canAccessHrManagement — klucz HrAccess + widoczność modułu hrManagement (bramka landingu /obsluga-hr);
 *   canAccessItamOverview — ItamDeviceView lub ItamInvoiceView (bramka /itam/przeglad);
 *   canAccessProcedury — wejście na kafelek /admin/procedury-bezpieczenstwo (ten sam predykat co panel);
 *   canAccessAnalitykaCzasu — wejście na kafelek /admin/worktime-analytics;
 *   canAccessMojDzial — klucz ModuleMojDzial (Mój Dział → Procedury).
 * @returns {{ cel: string, etykieta: string } | null} `null` = brak przycisku (strona bez rodzica).
 */
export function celPowrotuZAdresu(pathname, {
    stateFrom = null,
    search = "",
    canAccessTickets = true,
    canAccessItManagement = canAccessTickets,
    canAccessHrManagement = true,
    canAccessItamOverview = true,
    canAccessProcedury = true,
    canAccessAnalitykaCzasu = true,
    canAccessMojDzial = true,
} = {}) {
    // 1. Landing obsługi albo panel /admin, z którego przyszliśmy, ma pierwszeństwo (dotychczasowe zachowanie Header.js).
    if (stateFrom === "itManagement" || stateFrom === "hrManagement" || stateFrom === "admin") return celTrasy(stateFrom);

    const p = String(pathname ?? "").replace(/\/+$/, "");
    if (!p || p === "/") return null;

    const landingIt = () => (canAccessItManagement ? celTrasy("itManagement") : celTrasy("dashboard"));
    const landingHr = () => (canAccessHrManagement ? celTrasy("hrManagement") : celTrasy("dashboard"));
    const doc = Number(new URLSearchParams(String(search ?? "")).get("doc")) || null;

    // 2. Karta zgłoszenia → kolejka; karta sprawy onboardingu → lista onboardingu (jedyne adresy parametryczne drzew obsługi,
    //    dla których „poziom wyżej” to lista, nie landing).
    if (parametryTrasy(p, "ticketDetail")) return celTrasy("tickets");
    if (parametryTrasy(p, "hrOnboardingCase")) return celTrasy("hrOnboarding", "Onboarding");

    // 3. Kolejki ze stroną wbudowaną.
    if (dokladnieTrasa(p, "tickets") || podTrasa(p, "tickets")) return landingIt();
    if (dokladnieTrasa(p, "hrTickets") || podTrasa(p, "hrTickets")) return landingHr();

    // 4. Drzewo obsługi IT: podstrony modułu DYNAMICZNEGO wracają na jego landing (/obsluga-<kod>, za samym TicketView),
    //    statyczne i moduł IT (itsm/tickets) — na /obsluga-it (TicketView + widoczność modułu). Bez klucza — pulpit.
    if (podTrasa(p, "itManagement")) {
        const baza = adresyTrasy("itManagement").find((a) => p.startsWith(a + "/"));
        const kod = p.slice(baza.length + 1).split("/")[0];
        if (kod && !statyczneSegmentyObslugiIt().has(kod)) {
            const k = bezpiecznieDekoduj(kod);
            if (!KODY_IT.includes(k.toLowerCase())) return canAccessTickets ? celModulu(k) : celTrasy("dashboard");
        }
        return landingIt();
    }
    // Drzewo obsługi HR: każda podstrona wraca na landing /obsluga-hr (z kluczem HrAccess) albo na pulpit (bez).
    if (podTrasa(p, "hrManagement")) return landingHr();

    // 5. Tabela wyjątków drzew i tras parametrycznych.
    if (parametryTrasy(p, "adminEmployeeWorkTime")) return celTrasy("adminEmployees");
    if (podTrasa(p, "wykazKsc")) return celTrasy("wykazKsc");
    if (dokladnieTrasa(p, "wykazKsc")) return celTrasy("dashboard");
    // Procedury: /polityki jest WYŁĄCZNIE przekierowaniem zależnym od kluczy (PrzekierowaniePolityk) — członek zarządu bez klucza
    // redakcji trafiałby z karty decyzji na pusty ekran. Karta decyzji otwiera się z listy „Do decyzji” → tam wraca;
    // same listy decyzji i rejestracja decyzji → pulpit. Pozostałe adresy pod /polityki → /polityki (jak dotąd).
    // [domknięcie eb 2026-10-04] Karta otwarta z OKNA PROCEDURY (`?doc=ID`) wraca do procedury (etap 2) — dokładnie tam, dokąd
    // prowadzi „Wróć do procedury” na stronie (KartaDecyzjiPage): z kafelkiem modułu procedur do okna w /admin, kierownik działu do
    // Mój Dział → Procedury. Bez żadnego z nich strona cofa się w historii — nagłówek wtedy milczy, żeby nie pokazać innego celu.
    if (parametryTrasy(p, "politykiKartaDecyzji")) {
        if (!doc) return celTrasy("politykiDoDecyzji", "Do decyzji");
        if (canAccessProcedury) return { cel: sciezkaProcedury(doc, 2), etykieta: "Procedura" };
        if (canAccessMojDzial) return { cel: `${localizedPath("myDeptProcedury")}?doc=${doc}&etap=2`, etykieta: "Procedura" };
        return null;
    }
    // Rejestracja decyzji z papieru: strona wraca do okna procedury (z `?doc`) albo do listy „Procedury i bezpieczeństwo” (RejestracjaDecyzjiPage).
    if (dokladnieTrasa(p, "politykiRejestracja")) {
        if (!canAccessProcedury) return celTrasy("dashboard");
        const lista = localizedPath("adminModule", { moduleSlug: "procedury-bezpieczenstwo" });
        return doc ? { cel: `${lista}?doc=${doc}&etap=2`, etykieta: "Procedura" } : { cel: lista, etykieta: "Procedury i bezpieczeństwo" };
    }
    if (dokladnieTrasa(p, "politykiDoDecyzji")) return celTrasy("dashboard");
    if (podTrasa(p, "polityki")) return celTrasy("polityki");
    // Mój Dział: zakładki hubu wracają do hubu; sam pulpit hubu (/moj-dzial/pulpit = to, co otwiera /moj-dzial) — na pulpit aplikacji.
    if (dokladnieTrasa(p, "myDeptOverview")) return celTrasy("dashboard");
    if (adresyTrasy("myDeptOverview").map(bezOstatniegoSegmentu).some((h) => p.startsWith(h + "/")))
        return { cel: adresHubuMojegoDzialu(), etykieta: NAZWY_CELOW.myDept };
    if (dokladnieTrasa(p, "itamOverview")) return landingIt();
    // Pozostałe strony ITAM (urządzenia, faktury, protokoły, licencje, TeamViewer, SIM) → przegląd ITAM. Zbiór z ROUTE_MAP (klucze itam*).
    // Przegląd stoi za ItamDeviceView LUB ItamInvoiceView — bez nich (sam klucz licencji, TeamViewera, książki adresowej) poziom wyżej.
    if (Object.keys(ROUTE_MAP).some((k) => k.startsWith("itam") && (dokladnieTrasa(p, k) || podTrasa(p, k)))) {
        return canAccessItamOverview ? celTrasy("itamOverview") : landingIt();
    }
    // Szczegóły pracownika w analityce czasu pracy (trasa spoza ROUTE_MAP) → lista analityki, jak „Powrót” na stronie
    // (WorkTimeUserDetailPage). Przed ogólną regułą panelu, która dawała „Panel administracyjny”.
    const analityka = `${localizedPath("admin")}/worktime-analytics`;
    if (p.startsWith(`${analityka}/user/`) && p.split("/").length === 5) {
        return canAccessAnalitykaCzasu ? { cel: analityka, etykieta: "Analityka czasu pracy" } : celTrasy("admin");
    }
    /* [ui1006 2026-10-06] MIEJSCE STRONY WIELOOBSZAROWEJ → START TEJ STRONY, z jej tytułem. PRZED ogólną regułą panelu.
       BYŁO (do trasy z gwiazdką): każdy adres pod /admin/… wracał do „Panel administracyjny” — z miejsca „Incydenty”
       człowiek wyskakiwałby z całej strony Office 365, zamiast wrócić na jej start „Do załatwienia”.
       Sam /admin/<slug> (start strony) dalej wraca do panelu regułą niżej. Tytuł: `tytulStrony` — po przełączeniu strony
       tytuł z rejestru, przed przełączeniem dotychczasowy (ten, który człowiek widzi w nagłówku strony).
       Cel nie potrzebuje osłony klucza: start strony stoi za tą samą bramką kafelka co jej miejsca (AdminModuleRouter). */
    const stronaObszarow = stronaObszarowZAdresu(p);
    if (stronaObszarow) return { cel: `${localizedPath("admin")}/${stronaObszarow.strona}`, etykieta: tytulStrony(stronaObszarow) };
    if (podTrasa(p, "admin")) return celTrasy("admin");
    if (dokladnieTrasa(p, "admin")) return celTrasy("dashboard");
    // (/protocols z dawnego warunku Header.js nie ma trasy w aplikacji — adres martwy, pominięty świadomie.)
    if (dokladnieTrasa(p, "eTeczka")) return celTrasy("dashboard");

    // 6. Ogólny „poziom wyżej”: zdjąć ostatni segment; rodzic musi być STATYCZNYM wpisem ROUTE_MAP (nie przekierowaniem z parametrem).
    const rodzic = bezOstatniegoSegmentu(p);
    if (!rodzic || rodzic === "/") return null;
    for (const [klucz, r] of Object.entries(ROUTE_MAP)) {
        for (const adres of [r.pl, r.en]) {
            if (adres && !adres.includes(":") && adres === rodzic) return celTrasy(klucz);
        }
    }
    return null;
}

/**
 * [domknięcie eb 2026-10-04] OSŁONY CELU POWROTU z uprawnień — JEDNO miejsce dla nagłówka (Header.js) i stron z własnym
 * przyciskiem powrotu (ProceduryModuluPage). Każda osłona to lustro bramki trasy celu w App.js albo predykatu kafelka /admin,
 * więc przycisk „Powrót” nigdy nie prowadzi na ekran „Brak dostępu” ani „Moduł ukryty”.
 *
 * Brak `auth` (komponent poza AuthContext, np. w teście) = brak kluczy: cel spada na pulpit, a nie na cudzy landing.
 * @param {{ hasPermission?: Function, isModuleGloballyHidden?: Function, isModuleRestricted?: Function, user?: object, isAdmin?: boolean }} auth
 */
export function oslonyPowrotu(auth) {
    const ma = (klucz) => typeof auth?.hasPermission === "function" && !!auth.hasPermission(klucz);
    const ukryty = (kod) => (typeof auth?.isModuleGloballyHidden === "function" && !!auth.isModuleGloballyHidden(kod))
        || (typeof auth?.isModuleRestricted === "function" && !!auth.isModuleRestricted(kod));
    // Predykat kafelka pyta czasem o LISTĘ kluczy (dowolny z nich) — tak działa hasPermission z AuthContext; tu bez zależności od tego.
    const authKafelka = { ...(auth || {}), hasPermission: (k) => (Array.isArray(k) ? k.some(ma) : ma(k)) };
    const zakladkaPanelu = (slug) => {
        const kafelek = getTileBySlug(slug);
        return Boolean(kafelek) && !czyRouterOdmawiaKafelka(kafelek, authKafelka);
    };
    return {
        canAccessTickets: ma("TicketView"),
        canAccessItManagement: ma("TicketView") && !ukryty("itManagement"),
        canAccessHrManagement: ma("HrAccess") && !ukryty("hrManagement"),
        canAccessItamOverview: ["ItamDeviceView", "ItamInvoiceView"].some(ma),
        canAccessProcedury: zakladkaPanelu("procedury-bezpieczenstwo"),
        canAccessAnalitykaCzasu: zakladkaPanelu("worktime-analytics"),
        canAccessMojDzial: ma("ModuleMojDzial"),
    };
}

export default adresModuluObslugi;
